import { useEffect } from 'react';

/**
 * Short wheel ease for small notes. Chromium's own smooth scrolling is off
 * app-wide (electron/main.js) because a long ease trails the wheel. A ~75ms
 * ease is pleasant on a short page, but it runs on the main thread: every
 * tick reads layout, then animates scrollTop. On a long document that paint
 * can't keep up, so the motion stutters. Past a couple of screens we leave
 * the wheel to the browser instead.
 */

const smoothedScrollers = new WeakSet();
const overflowYCache = new WeakMap();

// Fraction of remaining distance covered each frame. Higher = snappier.
const EASE = 0.4;
const HEAVY_SCROLL_PX = 2500;
const HEAVY_SCROLL_SCREENS = 1.75;

export function scrollEaseFits(scrollHeight, clientHeight) {
  if (clientHeight <= 0) return false;
  return scrollHeight <= Math.max(HEAVY_SCROLL_PX, clientHeight * HEAVY_SCROLL_SCREENS);
}

function overflowY(el) {
  let oy = overflowYCache.get(el);
  if (oy === undefined) {
    oy = getComputedStyle(el).overflowY;
    overflowYCache.set(el, oy);
  }
  return oy;
}

/** A nested scroller only keeps the wheel when it can still move that way. */
export function nestedScrollerConsumesWheel(target, scroller, deltaY) {
  if (!deltaY || !target) return false;
  for (let n = target.nodeType === 1 ? target : target.parentElement; n && n !== scroller; n = n.parentElement) {
    const oy = overflowY(n);
    if (oy !== 'auto' && oy !== 'scroll') continue;
    const max = n.scrollHeight - n.clientHeight;
    if (max <= 1) continue;
    if (deltaY < 0) return n.scrollTop > 0;
    return n.scrollTop < max - 1;
  }
  return false;
}

function findScrollParent(node) {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const { overflowY: oy } = getComputedStyle(el);
    if (oy === 'auto' || oy === 'scroll') return el;
  }
  return null;
}

function wheelDelta(e, scroller) {
  if (e.deltaMode === 1) return e.deltaY * 16;
  if (e.deltaMode === 2) return e.deltaY * scroller.clientHeight;
  return e.deltaY;
}

export function useSmoothWheel(editorRootRef) {
  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return undefined;
    const scroller = findScrollParent(root);
    if (!scroller || smoothedScrollers.has(scroller)) return undefined;
    smoothedScrollers.add(scroller);

    let target = null;
    let expected = null;
    let raf = null;
    let listening = false;

    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      target = null;
      expected = null;
    };

    const step = () => {
      // Scroll anchoring or a scrollbar moved the page; don't fight it.
      if (expected !== null && Math.abs(scroller.scrollTop - expected) > 2) {
        stop();
        return;
      }
      const diff = target - scroller.scrollTop;
      if (Math.abs(diff) <= 1) {
        scroller.scrollTop = target;
        stop();
        return;
      }
      scroller.scrollTop += diff * EASE;
      expected = scroller.scrollTop;
      raf = requestAnimationFrame(step);
    };

    const onWheel = (e) => {
      if (e.ctrlKey || e.defaultPrevented) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const delta = wheelDelta(e, scroller);
      if (!delta) return;
      if (nestedScrollerConsumesWheel(e.target, scroller, delta)) return;
      const max = scroller.scrollHeight - scroller.clientHeight;
      if (max <= 0) return;
      e.preventDefault();
      if (target === null) target = scroller.scrollTop;
      target = Math.max(0, Math.min(max, target + delta));
      if (!raf) raf = requestAnimationFrame(step);
    };

    const attach = () => {
      if (listening) return;
      scroller.addEventListener('wheel', onWheel, { passive: false });
      listening = true;
    };
    const detach = () => {
      if (!listening) return;
      scroller.removeEventListener('wheel', onWheel);
      listening = false;
      stop();
    };
    const sync = () => {
      if (scrollEaseFits(scroller.scrollHeight, scroller.clientHeight)) attach();
      else detach();
    };

    // A pointer interaction must not fight a pending ease. Document-level
    // capture, NOT a scroller listener: the minimap navigator is portaled to
    // document.body, so grabbing its viewport never fires pointerdown inside
    // the scroller — the ease kept running and fought the drag every frame.
    const onPointerDown = () => stop();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(scroller);
    ro?.observe(root);
    sync();

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      stop();
      detach();
      ro?.disconnect();
      smoothedScrollers.delete(scroller);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [editorRootRef]);
}
